/**
 * Impersonation Provider - Admin Impersonation Context
 *
 * Manages admin impersonation sessions for customer support
 *
 * SECURITY FEATURES:
 * - Super admin only (verified by Edge Functions)
 * - 24-hour time limits with auto-expiry
 * - User activity tracking (Bug #1)
 * - Multi-tab sync with race condition prevention (Bug #5)
 * - Network failure cleanup (Bug #3)
 * - Organization cache clearing (Bug #12)
 *
 * INTEGRATION:
 * - supabaseCloudClient reads targetOrgId from localStorage
 * - auditWrapper.js logs actions during impersonation
 * - ImpersonationBanner displays current session
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
// TODO: Migrate to Azure impersonation APIs (POST /api/admin/impersonate/start, /end)
// import { getSupabaseClient } from '../../lib/supabase';
// import { clearOrganizationCache } from '../../sync/supabaseCloudClient';
import { devLog } from '../../utils/devLog';

// Session type definition
export interface ImpersonationSession {
  sessionId: string;
  targetOrganizationId: string;
  organizationName: string;
  startedAt: string;
  expiresAt: string;
  adminEmail?: string;
}

// Context type
interface ImpersonationContextType {
  session: ImpersonationSession | null;
  loading: boolean;
  error: string | null;
  startImpersonation: (targetOrgId: string, orgName: string, reason: string) => Promise<void>;
  endImpersonation: () => Promise<void>;
  isImpersonating: boolean;
  timeRemaining: number; // milliseconds until expiry
}

// Create context
const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

// Storage keys
const SESSION_KEY = 'sr.impersonation.session';
const TARGET_ORG_KEY = 'sr.impersonation.targetOrgId';
const ENDING_FLAG_KEY = 'sr.impersonation.ending';
const LAST_ACTIVITY_KEY = 'sr.impersonation.lastActivity';

// User activity tracking (Bug #1)
const IDLE_THRESHOLD = 30000; // 30 seconds of no activity
const ACTIVITY_UPDATE_INTERVAL = 10000; // Update every 10 seconds

interface ImpersonationProviderProps {
  children: ReactNode;
}

export function ImpersonationProvider({ children }: ImpersonationProviderProps) {
  const [session, setSession] = useState<ImpersonationSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);

  // Refs for activity tracking (Bug #1)
  const lastActivityRef = useRef<number>(Date.now());
  const activityListenersAttached = useRef<boolean>(false);

  // ============================================
  // User Activity Tracking (Bug #1)
  // ============================================

  useEffect(() => {
    if (!session || activityListenersAttached.current) return;

    const updateActivity = () => {
      lastActivityRef.current = Date.now();
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    };

    // Track user interactions
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    activityListenersAttached.current = true;

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      activityListenersAttached.current = false;
    };
  }, [session]);

  // ============================================
  // Load Session from Storage on Mount
  // ============================================

  useEffect(() => {
    const loadSession = () => {
      try {
        const stored = localStorage.getItem(SESSION_KEY);
        if (stored) {
          const sessionData: ImpersonationSession = JSON.parse(stored);

          // Check if expired
          const expiresAt = new Date(sessionData.expiresAt);
          if (expiresAt <= new Date()) {
            devLog('[IMPERSONATE] Session expired - cleaning up');
            cleanupSession();
            return;
          }

          setSession(sessionData);
          devLog('[IMPERSONATE] Session loaded from storage', {
            orgId: sessionData.targetOrganizationId,
            expires: sessionData.expiresAt
          });
        }
      } catch (error) {
        console.error('[IMPERSONATE] Failed to load session:', error);
      }
    };

    loadSession();
  }, []);

  // ============================================
  // Multi-Tab Sync (Bug #5)
  // ============================================

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Session ended in another tab
      if (e.key === SESSION_KEY && e.newValue === null) {
        devLog('[IMPERSONATE] Session ended in another tab');

        // Check if we initiated the end (Bug #5)
        const endingFlag = localStorage.getItem(ENDING_FLAG_KEY);
        if (!endingFlag) {
          // Another tab ended it - just update state
          setSession(null);
          setError(null);
        }
      }

      // Session started in another tab
      if (e.key === SESSION_KEY && e.newValue) {
        try {
          const newSession: ImpersonationSession = JSON.parse(e.newValue);
          setSession(newSession);
          devLog('[IMPERSONATE] Session started in another tab');
        } catch (error) {
          console.error('[IMPERSONATE] Failed to parse session from storage event');
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // ============================================
  // Start Impersonation
  // ============================================

  const startImpersonation = async (
    targetOrgId: string,
    orgName: string,
    reason: string
  ): Promise<void> => {
    // TODO: Migrate to Azure impersonation APIs (POST /api/admin/impersonate/start)
    setLoading(false);
    const errorMessage = 'Impersonation temporarily unavailable during Azure migration. Will be restored in Phase 7-8.';
    setError(errorMessage);
    throw new Error(errorMessage);

    /* ORIGINAL SUPABASE CODE - TO BE REPLACED WITH AZURE API
    setLoading(true);
    setError(null);

    try {
      const supabase = await getSupabaseClient();
      if (!supabase) {
        throw new Error('Supabase client not available');
      }

      // Call Edge Function
      console.log('[IMPERSONATE] Calling Edge Function with:', {
        targetOrganizationId: targetOrgId,
        reason: reason,
        reasonLength: reason?.length
      });

      const { data, error: fnError } = await supabase.functions.invoke('impersonate-start', {
        body: {
          targetOrganizationId: targetOrgId,
          reason
        }
      });

      console.log('[IMPERSONATE] Edge Function response:', { data, fnError });

      // Network failure handling (Bug #3)
      if (fnError) {
        console.error('[IMPERSONATE] Edge Function error:', fnError);
        // The actual error message from Edge Function is in data.error, not fnError.message
        const errorMessage = data?.error || fnError.message || 'Failed to start impersonation session';
        throw new Error(errorMessage);
      }

      if (!data || !data.success) {
        throw new Error(data?.error || 'Failed to start impersonation session');
      }

      // Verify Edge Function returned all required fields (Bug #3)
      const { sessionId, targetOrganizationId, startedAt, expiresAt } = data;
      if (!sessionId || !targetOrganizationId || !startedAt || !expiresAt) {
        throw new Error('Invalid session data returned from server');
      }

      const newSession: ImpersonationSession = {
        sessionId,
        targetOrganizationId,
        organizationName: orgName,
        startedAt,
        expiresAt,
        adminEmail: data.adminEmail
      };

      // Save to localStorage AFTER Edge Function succeeds (Bug #3)
      localStorage.setItem(SESSION_KEY, JSON.stringify(newSession));
      localStorage.setItem(TARGET_ORG_KEY, targetOrganizationId);
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));

      // Set working organization (persists after impersonation ends)
      localStorage.setItem('sr.working.targetOrgId', targetOrganizationId);
      console.log('[IMPERSONATE] Set working org to:', targetOrganizationId);

      // Verify localStorage writes succeeded (Bug #3)
      const stored = localStorage.getItem(SESSION_KEY);
      if (!stored) {
        throw new Error('Failed to save session to localStorage');
      }

      // Clear organization cache to force refresh with new targetOrgId
      clearOrganizationCache();

      setSession(newSession);
      devLog('[IMPERSONATE] Session started successfully', newSession);

      // Reload to apply impersonation
      window.location.reload();

    } catch (error: any) {
      console.error('[IMPERSONATE] Start failed:', error);
      setError(error.message);

      // Cleanup on failure (Bug #3)
      cleanupSession();

      throw error;
    } finally {
      setLoading(false);
    }
    */
  };

  // ============================================
  // End Impersonation
  // ============================================

  const endImpersonation = async (): Promise<void> => {
    // TODO: Migrate to Azure impersonation APIs (POST /api/admin/impersonate/end)
    if (!session) {
      devLog('[IMPERSONATE] No active session to end');
      return;
    }

    // For now, just clear local session without backend call
    cleanupSession();
    devLog('[IMPERSONATE] Local session cleared (backend call disabled during migration)');
    window.location.href = '/admin';
    return;

    /* ORIGINAL SUPABASE CODE - TO BE REPLACED WITH AZURE API
    setLoading(true);

    try {
      // Set ending flag FIRST (Bug #5)
      localStorage.setItem(ENDING_FLAG_KEY, 'true');

      const supabase = await getSupabaseClient();
      if (!supabase) {
        throw new Error('Supabase client not available');
      }

      // Call Edge Function (idempotent - won't fail if already ended)
      const { data, error: fnError } = await supabase.functions.invoke('impersonate-end', {
        body: {
          sessionId: session.sessionId
        }
      });

      if (fnError) {
        console.error('[IMPERSONATE] Edge Function error:', fnError);
        // Don't throw - still cleanup locally
      }

      // Clear localStorage
      cleanupSession();

      // Clear organization cache BEFORE reload (Bug #12)
      clearOrganizationCache();

      devLog('[IMPERSONATE] Session ended successfully');

      // Redirect to admin panel
      window.location.href = '/admin';

    } catch (error: any) {
      console.error('[IMPERSONATE] End failed:', error);
      setError(error.message);

      // Still cleanup even if Edge Function failed
      cleanupSession();
      clearOrganizationCache();

      // Still redirect
      window.location.href = '/admin';
    } finally {
      setLoading(false);
      // Cleanup ending flag after reload
      setTimeout(() => {
        localStorage.removeItem(ENDING_FLAG_KEY);
      }, 1000);
    }
    */
  };

  // ============================================
  // Cleanup Helper (MUST be defined before handleExpiry)
  // ============================================

  const cleanupSession = useCallback(() => {
    console.log('🧹 [IMPERSONATE] Starting cleanup...');

    // Log BEFORE cleanup
    console.log('📋 BEFORE cleanup:', {
      session: localStorage.getItem(SESSION_KEY),
      targetOrg: localStorage.getItem(TARGET_ORG_KEY),
      workingOrg: localStorage.getItem('sr.working.targetOrgId'),
      currentUser: localStorage.getItem('current_user')
    });

    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(TARGET_ORG_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);

    // Check if current user is Super Admin (organizationId: null)
    // Super Admin should NOT preserve working org after exiting impersonation
    try {
      const currentUser = JSON.parse(localStorage.getItem('current_user') || '{}');
      console.log('👤 Current user check:', {
        email: currentUser.email,
        organizationId: currentUser.organizationId,
        role: currentUser.role,
        isSuperAdmin: !currentUser.organizationId
      });

      if (!currentUser.organizationId) {
        // Super Admin: Clear working org ONLY
        console.log('🔴 SUPER ADMIN DETECTED - CLEARING WORKING ORG');
        localStorage.removeItem('sr.working.targetOrgId');

        // NOTE: We do NOT clear IndexedDB because:
        // 1. Super Admin has no data of their own (organizationId is null)
        // 2. IndexedDB contains the ORGANIZATION'S real data
        // 3. Clearing it would delete the organization's crew/equipment/etc
        // 4. After clearing working org, getOrgId() returns 'default-org'
        // 5. No data exists at 'default-org' keys, so Super Admin sees empty lists
        // This is the correct behavior - preserve org data, just change the view context

        console.log('[IMPERSONATE] Cleanup complete, working org CLEARED (Super Admin)');
      } else {
        // Regular user: Preserve working org
        console.log('🔵 REGULAR USER - PRESERVING WORKING ORG');
        console.log('[IMPERSONATE] Cleanup complete, working org preserved (Regular user)');
      }
    } catch (e) {
      console.error('[IMPERSONATE] Error checking user org:', e);
      // On error, clear working org to be safe
      localStorage.removeItem('sr.working.targetOrgId');
    }

    // Log AFTER cleanup
    console.log('✅ AFTER cleanup:', {
      session: localStorage.getItem(SESSION_KEY),
      targetOrg: localStorage.getItem(TARGET_ORG_KEY),
      workingOrg: localStorage.getItem('sr.working.targetOrgId'),
      currentUser: localStorage.getItem('current_user')
    });

    setSession(null);
    setTimeRemaining(0);
    console.log('🧹 [IMPERSONATE] Cleanup complete!');
  }, [setSession, setTimeRemaining]); // Stable setState functions

  // ============================================
  // Handle Session Expiry
  // ============================================

  const handleExpiry = useCallback(() => {
    devLog('[IMPERSONATE] Session expired - forcing end');
    cleanupSession();
    setError('Impersonation session has expired');

    // Redirect to admin panel
    setTimeout(() => {
      window.location.href = '/admin';
    }, 2000);
  }, [cleanupSession, setError]); // Memoize to prevent stale closures

  // ============================================
  // Auto-Expiry Check with Activity Tracking (Bug #1)
  // ============================================

  useEffect(() => {
    if (!session) {
      setTimeRemaining(0);
      return;
    }

    const checkExpiry = () => {
      const now = Date.now();
      const expiresAt = new Date(session.expiresAt).getTime();
      const remaining = expiresAt - now;

      setTimeRemaining(Math.max(0, remaining));

      // Check if user is idle (Bug #1)
      const lastActivity = lastActivityRef.current;
      const idleTime = now - lastActivity;
      const isIdle = idleTime > IDLE_THRESHOLD;

      // Only show expiry warning if user is idle
      if (remaining <= 0) {
        devLog('[IMPERSONATE] Session expired');
        handleExpiry();
      } else if (remaining <= 10000 && isIdle) {
        // 10 second warning (only when idle)
        devLog('[IMPERSONATE] Session expiring in 10 seconds');
      }
    };

    // Check every second
    const interval = setInterval(checkExpiry, 1000);
    checkExpiry(); // Initial check

    return () => clearInterval(interval);
  }, [session, handleExpiry]); // Include handleExpiry to prevent stale closures

  // ============================================
  // Context Value
  // ============================================

  const value: ImpersonationContextType = {
    session,
    loading,
    error,
    startImpersonation,
    endImpersonation,
    isImpersonating: !!session,
    timeRemaining
  };

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
}

// ============================================
// Custom Hook
// ============================================

export function useImpersonation(): ImpersonationContextType {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within ImpersonationProvider');
  }
  return context;
}
